import React from 'react'
import {Button, ContextMenu, Li, ListSeparator, Icon, Loading, Dialog} from 'startlibs/lib/components'
import {createSelector, defaultMemoize} from 'reselect'
import {ice} from 'icepath'
import {withToggles, withPopupToggles, fetchConnect, withRouteTo} from 'startlibs/lib/hocs'
import {isSmallDevice, smoothScroll} from 'startlibs/lib/lib'
import {getReports} from '../selectors'
import {getFetcher} from 'startlibs'
import {deleteReport, loadReports, setNotifications} from '../reducers'
import styled from 'styled-components'
import {en_US, dicomDateToDate, formatDate} from 'report-components'
import {connect} from 'react-redux'
import {sendEmailFetcher} from '../report/ReportViewerHeader'
import {SendEmailDialog} from 'report-components'
import {ReportInfoCard} from './ReportInfoCard'

const SEARCH_TIMEOUT = 300
const LOAD_MORE_WHEN_PX_SCROLL_TO_BOTTOM = 200
const EMPTY_REPORT_RECIPIENTS = {registeredUsers:[],suggestedUsers:[]}
// const DEV_TEST_ALWAYS_RELOAD = false//process.env.NODE_ENV === 'development'

const ReportsLoader = styled(Loading)`
  margin: 3rem auto 0;
`


const DropdownButton = styled(Button)`
  ${Button} ~ & {
    margin-left: .5rem;
  }
  width: 2rem;
  padding: 0;
  span {
    transform: translate(50%,-50%);
    right: 50%;
  }
`

const DeleteReportDialog = styled(Dialog)`
  .card {
    margin-bottom: 1rem;
  }
`

const reportsQueryFilter = defaultMemoize(
  (reports, query) => query ? reports.filter(r => r.patientName.toLowerCase().indexOf(query.toLowerCase()) >= 0 || r.studydesc.toLowerCase().indexOf(query.toLowerCase()) >= 0) : reports
)

const compare = (a, b) => a === b ? 0 : (a < b ? -1 : 1)

const defaultSort = (selector) => (a, b) => compare(selector(a), selector(b))

const reportsSorting = (sortingType) =>
  sortingType === 'patientName' ? defaultSort((e) => e.patientName) : defaultSort((e) => -e.lastSaved)


const reportSelector = createSelector(
  getReports,
  (_, props) => props,
  (reports, {query, sorting}) => ice.sort(reportsQueryFilter(reports, query.isOpen), reportsSorting(sorting.isOpen))
)

const sortBooleanToQueryString = (sort) => sort ? 'patient_name,asc' : 'last_Saved,desc'

@withToggles('query', 'sorting', 'isLoading','searching')
@withPopupToggles('sortingPopup')
@fetchConnect((state, props) => ({
  reportsMeta: state.reports,
  user: state.user,
  reports: reportSelector(state, props)
}), {connectFetcher: loadReports, loadReports})
export class Dashboard extends React.PureComponent {

  state = {isMobile: isSmallDevice(470)}

  setQuery = (e) => {
    this.props.query.open(e.target.value)
    smoothScroll(0, 500)
  }


  componentDidMount() {
    getFetcher("/api/pdfrenderer").then((pdfRenderer) => {
      this.setState({pdfRenderer})
      this.loadMoreReports()
    })
    window.addEventListener('resize', this.resizeEvent)
    window.addEventListener('scroll', this.loadMoreReports)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeEvent)
    window.removeEventListener('scroll', this.loadMoreReports)
  }

  resizeEvent = () => {
    if (this.state.isMobile !== isSmallDevice(470)) {
      this.setState({isMobile:isSmallDevice(470)})
    }
  }

  loaderPromise = false
  queryThrotle = -1

  componentDidUpdate(prevProps) {
    if (this.props.sorting.isOpen !== prevProps.sorting.isOpen) {
      // if (DEV_TEST_ALWAYS_RELOAD || !this.props.reportsMeta.finished) {
      this.props.loadReports({content:[],first:true}, {_dontFetch: true})
      // }
      this.loadMoreReports(true)
    } else if (this.props.query.isOpen !== prevProps.query.isOpen) {
      clearTimeout(this.queryThrotle)
      this.props.searching.open()
      this.queryThrotle = setTimeout(() => this.loadMoreReports(true), SEARCH_TIMEOUT)
    } else {
      this.loadMoreReports()
    }
  }

  loadMoreReports = (resetPagesOrEvent) => {
    const resetPages = resetPagesOrEvent === true
    const {isLoading, searching, loadReports, query, sorting, reportsMeta: {finished, page, total, query: usedQuery}} = this.props
    this.loaderPromise = resetPages ? null : this.loaderPromise
    if ((finished && !resetPages) || this.loaderPromise || (total === 0 && !usedQuery)) {
      return
    }
    if (window.innerHeight + window.scrollY + LOAD_MORE_WHEN_PX_SCROLL_TO_BOTTOM < document.body.scrollHeight) return
    const sort = sortBooleanToQueryString(sorting.isOpen)
    isLoading.open()
    const nextPage = resetPages ? 0 : page + 1
    const thisLoader = this.loaderPromise = getFetcher('/api/reports', {
      page: nextPage,
      size: 40,
      query: query.isOpen || undefined,
      sort
    }).then((result) => {
      if (thisLoader === this.loaderPromise) {
        isLoading.close()
        searching.close()
        this.loaderPromise = false
        loadReports(result, {_dontFetch: true, _payload: {page: nextPage, query: query.isOpen || undefined, sort}})
      }
    })
  }

  render() {
    const {reportsMeta, isLoading, reports, query, sorting, user, searching, sortingPopup} = this.props
    const {isMobile, pdfRenderer} = this.state
    return (
      <div className="content dashboard">
        <div className="header-container">
          <div className="header heading">
            <h4>{query.isOpen ? (searching.isOpen ? 'Searching...' : 'Search results') : 'Your reports'}</h4>
            {!((isLoading.isOpen && !reports.length) || searching.isOpen) && <div className="found-results">{reportsMeta.total} report{reports.length > 1 && "s"} found</div>}
          </div>
          <div className="header actions">
            <div className={"search-with-icon"+(searching.isOpen ? " search-loading" : "")}>
              <input type="text" placeholder="Search" value={query.isOpen || ''} onChange={this.setQuery}/>
              <span className="icon loader"/>
            </div>
            <div>
              <Button outline withDropdown={!isMobile} onlyIcon={isMobile} icon={isMobile ? 'sort' : undefined}
                      onClick={sortingPopup.toggle}>
                {!isMobile &&
                <React.Fragment>Sort by {sorting.isOpen ? 'patient name' : 'date modified'}</React.Fragment>}
                {
                  sortingPopup.isOpen &&
                  <ContextMenu fixed>
                    <Li icon={!sorting.isOpen ? 'check' : undefined} onClick={sorting.close} label="Date modified"/>
                    <Li icon={sorting.isOpen === 'patientName' ? 'check' : undefined}
                        onClick={() => sorting.open('patientName')} label="Patient name"/>
                  </ContextMenu>
                }
              </Button>
            </div>
          </div>
        </div>
        <div className="card-list two-columns">
          {
            reports.map(report => <ReportCard key={report.uid} user={user} pdfRenderer={pdfRenderer} report={report}/>)
          }
        </div>
        {
          (isLoading.isOpen && !searching.isOpen) ?
            <ReportsLoader size={36} borderWidth={5}/> :
            reportsMeta.total === 0 ?
              reportsMeta.query ?
                <div className="no-results-message">
                  <h2>No reports matched your search query.</h2>
                  <p>Try searching for a different term or return to home and <a onClick={query.close}>view all
                    reports.</a></p>
                  <Button onClick={query.close}>View all reports</Button>
                </div>
                :
                <div className="no-results-message">
                  <h2>No reports found.</h2>
                </div>
              : null
        }

      </div>
    )
  }
}


@withToggles('deleteReportDialog','sendViaEmail')
@withPopupToggles('reportContextMenu')
@connect((state)=>({deleteIsLoading: state.appUI.loadings[deleteReport]}),{deleteReport,setNotifications})
@withRouteTo
class ReportCard extends React.PureComponent {

  savePdfUrl= (save) => {
    const queryString = new URLSearchParams()
    queryString.append("save",save)
    queryString.append("endpoint",this.props.pdfRenderer.reportLoaderEnpoint)
    const url = this.props.pdfRenderer.reportRendererEndpoint + this.props.report.uid + "?" + queryString.toString()
    window.open(url,"_blank")
  }

  deleteReport = () => {
    this.props.deleteReport(this.props.report.uid)
      .then(() => {
        this.props.setNotifications('Report deleted successfully.')
      })
  }

  openJustSent = () => {
    this.props.setNotifications('Report successfully sent via e-mail.')
  }

  render() {
    const {report, deleteIsLoading, routeTo, user, sendViaEmail, deleteReportDialog, reportContextMenu} = this.props
    const lastSaved = formatDate(new Date(report.lastSaved), en_US, 'L dd, yyyy - hh:mm')
    const studyToDate = dicomDateToDate(report.studydate)
    const studydate = isNaN(studyToDate.getTime()) ? report.studydate : formatDate(studyToDate, en_US, 'L dd, yyyy')
    return (
      <a href={'/report/' + report.uid} className="card with-icon clickable"
         onClick={(e) => e.ctrlKey || e.metaKey ? undefined : e.preventDefault() || routeTo('/report/' + report.uid)}>
        <div className="card-content">
          <div className="buttons-container" onClick={(e) => {e.stopPropagation(); e.preventDefault(); reportContextMenu.close()}}>
            {
              report.finished
              ? <Button small icon="view" onClick={(e) => e.ctrlKey || e.metaKey ? undefined : e.preventDefault() || routeTo('/report/' + report.uid)}>View</Button>
              : <Button small icon="edit" onClick={(e) => e.ctrlKey || e.metaKey ? undefined : e.preventDefault() || routeTo('/report/' + report.uid)}>Edit</Button>
            }
            <DropdownButton
              small
              withDropdown
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); reportContextMenu.toggle()}}
            >
              {
                reportContextMenu.isOpen &&
                <ContextMenu>{report.finished &&
                    [
                      <Li key="email" onClick={sendViaEmail.open} label="Send via email" icon="email"/>,
                      <Li key="download" onClick={() => this.savePdfUrl(true)} target="_blank" label="Download PDF" icon="download"/>,
                      <Li key="print" onClick={() => this.savePdfUrl(false)} label="Print" icon="print"/>,
                      <ListSeparator key="separator" />
                    ]
                  }
                  <Li onClick={deleteReportDialog.open} label="Delete report" icon="delete"/>
                </ContextMenu>
              }
          </DropdownButton>
          </div>
          <div className="card-icon"><Icon icon={report.finished ? 'checked-report' : 'report'}/></div>
          <div className="card-title">{report.patientName}</div>
          <div className="card-detail"><b>Study: </b>{report.studydesc} ({studydate})</div>
          <div className="card-detail"><b>Referred by: </b>{report.refphysician ? (report.refphysician) : "Unknown"}</div>
        </div>
        <div className="card-footer">{(report.finished ? 'Finished on ' : 'Last draft saved on ') + lastSaved}</div>
        {
          deleteReportDialog.isOpen &&
            <DeleteReportDialog
              closeDialog={deleteReportDialog.close}
              title="Confirm report deletion"
            >
              <Dialog.Content>
                <p>You are about to delete the following report and all its content:</p>
                  <ReportInfoCard  report={report}/>
                <p>
                  <strong>Are you sure you want to delete it?</strong> Once confirmed this cannot be undone.
                </p>
              </Dialog.Content>
              <Dialog.Footer>
                <Button onClick={deleteReportDialog.close}>Cancel</Button>
                <Button isLoading={deleteIsLoading} alert onClick={this.deleteReport}>Confirm deletion</Button>
              </Dialog.Footer>
            </DeleteReportDialog>
        }
        {
          sendViaEmail.isOpen &&
          <SendEmailDialog
            reportRecipients={EMPTY_REPORT_RECIPIENTS}
            closeDialog={sendViaEmail.close}
            confirmSent={this.openJustSent}
            userEmail={user.email}
            attachedReport
            reportInfoCard={<ReportInfoCard report={report}/>}
            sendEmailFetcher={sendEmailFetcher(report.uid)}
          />
        }
      </a>
    )
  }
}



// WEBPACK FOOTER //
// ./src/javascripts/dashboard/Dashboard.jsx